import type {
  CompanyCardDto,
  CompanySource,
  GovernanceRoleDto,
  IdealItemDto,
  ResearchState,
  StepGuideDto,
} from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { api, errorMessage } from '../../../lib/api';
import { personName } from '../../../lib/format';
import { useTenant, useTenantQuery } from '../../../lib/useTenant';
import { Button, ErrorText } from '../../../ui';
import { Governance } from '../Governance';
import { ActivityLoader } from '../Onboarding';
import { useSetupProgress } from '../Setup';
import { StepIntro } from './Illustration';
import { useTalk } from './Talk';
import { AddedItems } from './AddedItems';
import { BrainNotes } from './StepReview';
import { OrgIdeal } from './OrgIdeal';

const FROM: Record<CompanySource, string> = {
  visura: 'dalla visura',
  persona: 'detto da te',
  documento: 'da un tuo documento',
  posta: 'dalla posta',
  sito: 'dal vostro sito',
  registro: 'da una pagina pubblica',
  online: 'da una pagina online sulla sede',
  supposto: 'supposto dal cervello',
};
/** Chi ricopre la carica, con il nome di persona leggibile anche se il registro lo scrive in maiuscolo. */
const who = (o: GovernanceRoleDto) =>
  o.tipo_titolare === 'persona' ? personName(o.titolare) : o.titolare;
const norm = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[’`]/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

/**
 * Stesso ruolo: stesso nome, o un nome abbastanza lungo contenuto nell'altro («Responsabile commerciale» e
 * «Responsabile commerciale energia e gas»).
 */
function same(a: string, b: string): boolean {
  const x = norm(a);
  const y = norm(b);
  if (x === y) return true;
  const [short, long] = x.length < y.length ? [x, y] : [y, x];
  return short.length >= 10 && long.includes(short);
}

/**
 * Passo «Organizzazione e ruoli» (ADR 0021, 0024, 0067): chi guida l'impresa oggi, l'organigramma ideale (lo stesso del
 * riepilogo, mai il confronto con ciò che c'è già: nulla si toglie) e le ipotesi da cui è partito il cervello. Da dove vengono i dati resta chiuso in fondo; per parlarne c'è «Parliamone» (ADR 0063). Le persone non
 * entrano nella configurazione: arrivano dopo l'acquisto (ADR 0036).
 */
export function OrganizationStep({
  next,
  onNext,
}: {
  next: { titolo: string } | null;
  onNext: () => void;
}) {
  const { t, tenant, isManager } = useTenant();
  const qc = useQueryClient();
  const card = useTenantQuery<CompanyCardDto>(['company'], '/company');
  const guide = useTenantQuery<StepGuideDto>(
    ['guide', 'organizzazione'],
    '/onboarding/guide/organizzazione',
  );
  const refresh = useMutation({
    mutationFn: () =>
      api<StepGuideDto>(t('/onboarding/guide/organizzazione/refresh'), { method: 'POST' }),
    onSuccess: (g) => qc.setQueryData(['tenant', tenant.id, 'guide', 'organizzazione'], g),
  });
  const [fixGov, setFixGov] = useState(false);
  const talk = useTalk();
  const [sources, setSources] = useState(false);

  const g = guide.data;
  const d = g?.ideale ?? null;
  const offices = (card.data?.cariche ?? []).filter((x) => x.stato === 'in_carica');
  const notes = [
    ...new Map(
      [
        ...(d?.impresa?.note ?? []),
        ...(d?.sedi ?? []).flatMap((s) => [
          ...(s.sede_in_se?.note ?? []),
          ...s.attivita.flatMap((a) => a.note),
        ]),
      ].map((n) => [norm(n), n]),
    ).values(),
  ];
  const updating =
    refresh.isPending ||
    !!g?.generating ||
    !!d?.impresa?.generating ||
    !!d?.sedi.some((s) => s.generating);
  const correct = (text: string) => talk.open(text);
  // Passo fatto: in testa il riepilogo (ADR 0033), consiglio e ipotesi chiusi sotto. «Rivedi il passo» li riapre.
  const progress = useSetupProgress();
  const [review, setReview] = useState(false);
  const fatto = progress.data?.wizard.find((x) => x.chiave === 'organizzazione')?.stato === 'fatto';
  const summary = fatto && !review && !!card.data && !!d;
  // Chi ricopre le cariche che una voce rappresenta (ADR 0051), scelte dal cervello; per i progetti di prima, gli organi
  // per nome. Sono le cariche in essere dell'impresa, non persone da assegnare (ADR 0036).
  const holder = (v: IdealItemDto) => {
    const byId = v.cariche?.length ? offices.filter((o) => v.cariche!.includes(o.id)) : [];
    const hits = byId.length
      ? byId
      : v.natura === 'organo'
        ? offices.filter((o) => same(o.carica, v.nome) || (!!o.organo && same(o.organo, v.nome)))
        : [];
    if (!hits.length) return null;
    return {
      chi: hits.map((o) => `${who(o)}${o.quota ? ` ${o.quota}` : ''}`).join(', '),
      fonte: [...new Set(hits.map((o) => FROM[o.fonte]))].join(', '),
      poteri:
        hits
          .map((o) => o.poteri)
          .filter(Boolean)
          .join(' · ') || null,
    };
  };
  // Le cariche in essere che nessuna voce rappresenta (ADR 0101): rapporti con terzi come procure o revisori, a parte.
  // Solo dopo che il sistema le ha agganciate al progetto; i soci stanno tra chi guida l'impresa.
  const voci = d
    ? [
        ...(d.impresa?.voci ?? []),
        ...d.sedi.flatMap((s) => [
          ...(s.sede_in_se?.voci ?? []),
          ...s.attivita.flatMap((a) => a.voci),
        ]),
      ]
    : [];
  const used = new Set(voci.flatMap((v) => v.cariche ?? []));
  const mandates = used.size
    ? offices
        .filter(
          (o) =>
            !o.quota &&
            !used.has(o.id) &&
            !voci.some(
              (v) =>
                v.natura === 'organo' &&
                (same(o.carica, v.nome) || (!!o.organo && same(o.organo, v.nome))),
            ),
        )
        .map((o) => ({ carica: o.carica, chi: who(o), fonte: FROM[o.fonte] }))
    : [];
  const idealError = d?.impresa?.error ?? d?.sedi.find((s) => s.error)?.error ?? null;

  return (
    <div className="space-y-6 pb-8 pt-2">
      <StepIntro step="organizzazione" title="Com’è organizzata la tua azienda">
        <p className="mt-1 text-sm text-ink/60">
          {summary
            ? 'La tua azienda come dovrebbe essere: organigramma e funzionigramma, con quante persone servono.'
            : 'Chi la guida oggi e come dovrebbe essere organizzata: chi risponde a chi, in ogni area e in ogni sede.'}
        </p>
      </StepIntro>
      <BrainNotes step="organizzazione" d={d} what="l’organizzazione" />

      {review && fatto && (
        <p className="text-center">
          <button className="text-sm text-accent hover:underline" onClick={() => setReview(false)}>
            ← Torna al riepilogo
          </button>
        </p>
      )}

      {(card.isLoading || guide.isLoading) && (
        <p className="py-6 text-center text-sm text-ink/50">
          Raccolgo l’organizzazione della tua azienda…
        </p>
      )}

      {card.data && !summary && (
        <section className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="flex-1 text-sm font-semibold">Chi guida l’impresa oggi</h3>
            {isManager && (
              <button
                className="text-xs text-accent hover:underline"
                onClick={() => setFixGov(!fixGov)}
              >
                {fixGov ? 'Chiudi' : 'Correggi o aggiungi'}
              </button>
            )}
          </div>
          {fixGov ? (
            <Governance list={card.data.cariche} />
          ) : (
            <Leaders offices={offices} card={card.data} />
          )}
        </section>
      )}

      {d && card.data && (
        <OrgIdeal
          design={d}
          company={card.data.identita.ragione_sociale ?? 'La tua azienda'}
          addetti={
            card.data.identita.addetti != null
              ? {
                  n: card.data.identita.addetti,
                  al: card.data.identita.addetti_al
                    ? new Date(card.data.identita.addetti_al).toLocaleDateString('it-IT', {
                        month: 'short',
                        year: 'numeric',
                      })
                    : null,
                }
              : null
          }
          holder={holder}
          mandates={mandates}
          done={summary}
          updating={updating}
          error={updating ? null : idealError}
          onRetry={isManager ? () => refresh.mutate() : null}
          onTalk={isManager ? correct : null}
          onReview={isManager ? () => setReview(true) : null}
        />
      )}
      {(refresh.error || guide.error) && (
        <ErrorText>{errorMessage(refresh.error ?? guide.error)}</ErrorText>
      )}

      {notes.length > 0 && (
        <Fold on={summary} title="Le mie ipotesi">
          <Hypotheses notes={notes} onCorrect={isManager ? correct : null} />
        </Fold>
      )}

      <div className="space-y-3">
        <details
          className="group border-t border-black/5 pt-3"
          open={sources}
          onToggle={(e) => setSources(e.currentTarget.open)}
        >
          <Summary>Da dove vengono questi dati</Summary>
          {sources && (
            <div className="mt-3 space-y-3 pl-5 text-sm">
              <OnlineResearch />
              {g && g.trovati.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-emerald-700">Ho già trovato</p>
                  <ul className="mt-1 space-y-0.5 text-xs text-ink/70">
                    {g.trovati.map((x) => (
                      <li key={x.cosa}>
                        ✓ {x.cosa} <span className="text-ink/45">· {x.fonte}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {g && g.mancano.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-ink/60">Manca</p>
                  <ul className="mt-1 space-y-0.5 text-xs text-ink/70">
                    {g.mancano.map((x) => (
                      <li key={x}>○ {x}</li>
                    ))}
                  </ul>
                </div>
              )}
              {isManager && !updating && (
                <button
                  className="text-xs text-accent hover:underline"
                  onClick={() => refresh.mutate()}
                >
                  Rifai il consiglio da capo
                </button>
              )}
            </div>
          )}
        </details>
      </div>

      {!!d && !updating && (
        <AddedItems
          step="organizzazione"
          intro="Se nella tua azienda c’è un ruolo che qui non vedi, o te ne serve uno, aggiungilo: resta tuo, e io lo completo con cosa fa, a chi risponde e con quali programmi lavora."
          label="Aggiungi un ruolo"
          placeholder="Es. responsabile commerciale"
          notePlaceholder="Es. segue i clienti grandi e le offerte"
        />
      )}

      <div className="flex flex-wrap items-center gap-3">
        <span className="flex-1" />
        {next && (
          <Button onClick={onNext}>
            Avanti<span className="hidden sm:inline">: {next.titolo}</span> →
          </Button>
        )}
      </div>
    </div>
  );
}

/** Nel riepilogo una parte si chiude sotto il suo titolo; altrimenti si mostra com'è. */
function Fold({ on, title, children }: { on: boolean; title: string; children: ReactNode }) {
  if (!on) return <>{children}</>;
  return (
    <details className="group border-t border-black/5 pt-3">
      <Summary>{title}</Summary>
      <div className="mt-3">{children}</div>
    </details>
  );
}

function Summary({ children }: { children: ReactNode }) {
  return (
    <summary className="flex cursor-pointer list-none items-center gap-2 text-xs text-ink/50 [&::-webkit-details-marker]:hidden">
      <span className="transition-transform group-open:rotate-90">▸</span>
      {children}
    </summary>
  );
}

/** Chi guida l'impresa oggi: le cariche in corso raggruppate (i soci in una riga), con i nomi veri e la fonte. */
function Leaders({ offices, card }: { offices: GovernanceRoleDto[]; card: CompanyCardDto }) {
  const groups = new Map<string, GovernanceRoleDto[]>();
  for (const o of offices) groups.set(norm(o.carica), [...(groups.get(norm(o.carica)) ?? []), o]);
  const id = card.identita;
  const staff = id.fonti.addetti;
  return (
    <div className="space-y-1.5 rounded-2xl border-2 border-emerald-400/50 bg-white px-4 py-3 text-sm">
      {!groups.size && (
        <p className="text-ink/50">
          Non so ancora chi guida l’impresa: carica la visura nel primo passo o dimmelo in
          «Parliamone».
        </p>
      )}
      {[...groups.values()].map((list) => (
        <p key={list[0]!.id} className="flex flex-wrap items-baseline gap-x-2">
          <b className="font-medium">{list[0]!.carica}</b>
          <span className="text-ink/75">
            {list.map((o) => `${who(o)}${o.quota ? ` (${o.quota})` : ''}`).join(', ')}
          </span>
          <span className="text-xs text-ink/40">{FROM[list[0]!.fonte]}</span>
        </p>
      ))}
      {id.addetti != null && (
        <p className="flex flex-wrap items-baseline gap-x-2 border-t border-black/5 pt-1.5">
          <b className="font-medium">Persone che ci lavorano</b>
          <span className="text-ink/75">
            {id.addetti}
            {id.addetti_al ? ` al ${id.addetti_al}` : ''}
          </span>
          {staff && <span className="text-xs text-ink/40">{FROM[staff.fonte]}</span>}
        </p>
      )}
    </div>
  );
}

/** Le ipotesi del consiglio, in un riquadro tranquillo: se una non è giusta si corregge in «Parliamone». */
function Hypotheses({
  notes,
  onCorrect,
}: {
  notes: string[];
  onCorrect: ((text: string) => void) | null;
}) {
  const [more, setMore] = useState(false);
  const shown = more ? notes : notes.slice(0, 4);
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold">Le mie ipotesi</h3>
      <p className="text-xs text-ink/50">
        Sono partito da queste. Se una non è giusta, dimmelo e rifaccio il consiglio.
      </p>
      <ul className="space-y-2 rounded-2xl border border-amber-200/70 bg-amber-50/40 px-4 py-3">
        {shown.map((n) => (
          <li key={n} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-ink/80">
            <span className="min-w-0 flex-1 basis-60">{n}</span>
            {onCorrect && (
              <button
                className="rounded-full bg-black/5 px-3 py-1 text-xs font-medium text-ink/70 transition hover:bg-black/10"
                onClick={() => onCorrect(`A proposito di «${n.replace(/[.;]\s*$/, '')}»: `)}
              >
                Correggimi
              </button>
            )}
          </li>
        ))}
        {notes.length > 4 && (
          <li>
            <button className="text-xs text-accent hover:underline" onClick={() => setMore(!more)}>
              {more ? 'Mostra meno' : `Mostra le altre ${notes.length - 4}`}
            </button>
          </li>
        )}
      </ul>
    </section>
  );
}

/** Ricerca online dell'impresa (ADR 0021-0022): parte da sola la prima volta, poi si rifà a richiesta. */
function OnlineResearch() {
  const { t, tenant } = useTenant();
  const qc = useQueryClient();
  const state = useTenantQuery<ResearchState>(['research'], '/onboarding/research', {
    refetchInterval: (d) => (d?.in_corso ? 1500 : false),
  });
  const start = useMutation({
    mutationFn: () => api<ResearchState>(t('/onboarding/research'), { method: 'POST' }),
    onSuccess: (r) => qc.setQueryData(['tenant', tenant.id, 'research'], r),
  });
  // L'avvio automatico e l'aggiornamento dei dati a ricerca finita li fa useAutoResearch nel corpo del passo.
  const r = state.data;
  if (!r) return null;
  const found = r.trovati;
  return (
    <div className="rounded-xl border border-accent/20 bg-accent/[0.04] px-4 py-3 text-sm">
      {r.in_corso ? (
        <ActivityLoader activity={r.activity} fallback="Cerco la tua azienda online…" />
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <span className="min-w-[14rem] flex-1">
            {start.error ? (
              <span className="text-red-700">{errorMessage(start.error)}</span>
            ) : r.errore ? (
              <span className="text-red-700">La ricerca online non è riuscita: {r.errore}</span>
            ) : r.eseguita_il ? (
              <>
                🔎 Ho cercato online il {new Date(r.eseguita_il).toLocaleDateString('it-IT')}:{' '}
                {r.pagine} {r.pagine === 1 ? 'pagina verificata' : 'pagine verificate'}
                {r.sito ? ` (sito ${r.sito.replace(/^https?:\/\//, '')})` : ''}
                {found
                  ? ` · ${found.sedi} sedi, ${found.attivita} attività, ${found.cariche} cariche, ${found.ruoli} ruoli, ${found.campi} dati dell’anagrafica`
                  : ''}
                .
                <span className="block text-xs text-ink/50">
                  Valgono solo le pagine in cui compare la tua partita IVA e il sito verificato
                  dell’azienda, e ogni dato è citato dalla sua pagina.
                </span>
              </>
            ) : (
              'Non ho ancora cercato la tua azienda online.'
            )}
          </span>
          <Button variant="ghost" disabled={start.isPending} onClick={() => start.mutate()}>
            {r.eseguita_il ? 'Cerca di nuovo online' : 'Cerca online'}
          </Button>
        </div>
      )}
    </div>
  );
}
